<script setup lang="ts">
import { FormSchema } from '@/@types/participant'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import Label from '@/components/ui/label/Label.vue'
import { useZipCode } from '@/composables/useZipCode'
import { formatCpf } from '@/helpers/document'
import { formatNumber, formatText } from '@/helpers/format-text'
import { formatZipcode } from '@/helpers/format-zipcode'
import { Loader2 } from 'lucide-vue-next'
import { computed, ref } from 'vue'
import CitySelect from './CitySelect.vue'
import StateSelect from './StateSelect.vue'

const { form } = defineProps<{ form: FormSchema; errors: Record<string, string> }>()

const { isLoading, searchAddressByZipCode } = useZipCode()

const storedCity = ref<string>('')

const onZipCodeSearch = async () => {
    const previousState = form.personal_info_attributes.state

    const result = await searchAddressByZipCode(form.personal_info_attributes.zip_code)
    if (!result) return

    form.personal_info_attributes.street = result.street
    form.personal_info_attributes.state = result.state
    form.personal_info_attributes.city = result.city
    form.personal_info_attributes.country = result.country

    if (previousState !== result.state) {
        storedCity.value = result.city
    }
}

const disabledButton = computed(() => {
    return !form.personal_info_attributes.zip_code || form.personal_info_attributes.zip_code.length != 9 || isLoading.value
})
</script>

<template>
    <fieldset class="grid gap-4 gap-x-8 lg:grid-cols-2">
        <legend class="mb-4 font-sans text-lg font-semibold text-gray-800">
            {{ $t('Personal information') }}
        </legend>

        <div class="flex flex-col gap-1.5">
            <Label required for="full_name">{{ $t('Full name') }}</Label>
            <Input
                type="text"
                id="full_name"
                v-model="form.personal_info_attributes.full_name"
                :placeholder="$t('Full name')"
                @input="(e: Event) => (form.personal_info_attributes.full_name = formatText((e.target as HTMLInputElement).value))"
            />
            <p v-if="errors.full_name" class="text-destructive text-xs">
                {{ errors.full_name }}
            </p>
        </div>
        <div class="flex flex-col gap-1.5">
            <Label for="linkedin">{{ $t('Linkedin') }}</Label>
            <Input type="text" id="linkedin" v-model="form.personal_info_attributes.linkedin" :placeholder="$t('Linkedin')" />
            <p v-if="errors.linkedin" class="text-destructive text-xs">
                {{ errors.linkedin }}
            </p>
        </div>
        <div class="flex flex-col gap-1.5">
            <div class="flex flex-col gap-1.5" v-if="!form.personal_info_attributes.has_foreign_doc">
                <Label for="cpf">{{ $t('CPF') }}</Label>
                <Input
                    type="text"
                    id="cpf"
                    v-model="form.personal_info_attributes.cpf"
                    :placeholder="$t('CPF')"
                    @input="(e: Event) => (form.personal_info_attributes.cpf = formatCpf((e.target as HTMLInputElement).value))"
                />
                <p v-if="errors.cpf" class="text-destructive text-xs">
                    {{ errors.cpf }}
                </p>
            </div>
            <div class="flex flex-col gap-1.5" v-else>
                <Label required for="foreign_doc">{{ $t('Foreign document') }}</Label>
                <Input type="text" id="foreign_doc" v-model="form.personal_info_attributes.foreign_doc" :placeholder="$t('Foreign document')" />
                <p v-if="errors.foreign_doc" class="text-destructive text-xs">
                    {{ errors.foreign_doc }}
                </p>
            </div>
            <div class="flex items-center gap-2">
                <Checkbox id="foreign-document" v-model="form.personal_info_attributes.has_foreign_doc" />
                <Label class="text-xs text-slate-500" for="foreign-document">{{ $t('I do not have a document issued in Brazil') }}</Label>
            </div>
        </div>
        <div class="flex flex-col gap-1.5">
            <Label required for="fone">{{ $t('Cellphone') }}</Label>
            <Input
                type="tel"
                id="fone"
                required
                v-model="form.personal_info_attributes.fone"
                :placeholder="$t('Cellphone')"
                maxlength="15"
                default-calling-code="BR"
            />

            <p v-if="errors['personal_info_attributes.fone'] || errors.fone" class="text-destructive text-xs">
                {{ errors['personal_info_attributes.fone'] || errors.fone }}
            </p>
        </div>

        <div class="flex flex-col gap-1.5">
            <Label for="another_phone">{{ $t('Phone') }}</Label>
            <Input
                type="tel"
                id="another_phone"
                required
                v-model="form.personal_info_attributes.another_phone"
                :placeholder="$t('Phone')"
                maxlength="15"
                default-calling-code="BR"
            />

            <p v-if="errors['personal_info_attributes.another_phone'] || errors.another_phone" class="text-destructive text-xs">
                {{ errors['personal_info_attributes.another_phone'] || errors.another_phone }}
            </p>
        </div>
        <div class="flex flex-col gap-1.5">
            <Label for="zip_code">{{ !form.personal_info_attributes.has_foreign_doc ? $t('Zipcode') : $t('Postal Code') }}</Label>
            <div class="flex gap-1.5">
                <Input
                    type="text"
                    id="zip_code"
                    v-model="form.personal_info_attributes.zip_code"
                    :placeholder="$t('Postal Code')"
                    @input="(e: Event) => (form.personal_info_attributes.zip_code = formatZipcode((e.target as HTMLInputElement).value))"
                    v-if="form.personal_info_attributes.has_foreign_doc"
                />
                <Input
                    type="text"
                    id="zip_code"
                    v-model="form.personal_info_attributes.zip_code"
                    :placeholder="$t('Zipcode')"
                    @input="(e: Event) => (form.personal_info_attributes.zip_code = formatZipcode((e.target as HTMLInputElement).value))"
                    v-else
                />
                <Button
                    class="w-full max-w-24"
                    type="button"
                    @click.prevent="onZipCodeSearch"
                    :disabled="disabledButton"
                    v-if="!form.personal_info_attributes.has_foreign_doc"
                >
                    <template v-if="!isLoading">
                        {{ $t('Search') }}
                    </template>
                    <template v-else>
                        <Loader2 class="animate-spin" />
                        <span class="sr-only">{{ $t('Searching') }}</span>
                    </template>
                </Button>
            </div>
            <p v-if="errors.zip_code" class="text-destructive text-xs">
                {{ errors.zip_code }}
            </p>
        </div>
        <div class="flex flex-col gap-1.5">
            <Label for="street">{{ $t('Address') }}</Label>
            <Input type="text" id="street" v-model="form.personal_info_attributes.street" :placeholder="$t('Address')" />
            <p v-if="errors.street" class="text-destructive text-xs">
                {{ errors.street }}
            </p>
        </div>
        <div class="flex flex-col gap-1.5">
            <Label for="number">{{ $t('Number') }}</Label>
            <Input
                type="text"
                id="number"
                v-model="form.personal_info_attributes.number"
                :placeholder="$t('Number')"
                @input="(e: Event) => (form.personal_info_attributes.number = formatNumber((e.target as HTMLInputElement).value))"
            />
            <p v-if="errors.number" class="text-destructive text-xs">
                {{ errors.number }}
            </p>
        </div>

        <StateSelect
            v-model="form.personal_info_attributes.state"
            :has-foreign-doc="form.personal_info_attributes.has_foreign_doc"
            :error="errors.state"
        />

        <CitySelect
            v-model="form.personal_info_attributes.city"
            :has-foreign-doc="form.personal_info_attributes.has_foreign_doc"
            :state="form.personal_info_attributes.state"
            :error="errors.city"
            :stored-city="storedCity"
            @update:stored-city="(value) => storedCity = value"
        />

        <div class="flex flex-col gap-1.5">
            <Label for="number">{{ $t('Complement') }}</Label>
            <Input type="text" id="number" v-model="form.personal_info_attributes.additional_info" :placeholder="$t('Complement')" />
            <p v-if="errors.additional_info" class="text-destructive text-xs">
                {{ errors.additional_info }}
            </p>
        </div>

        <div class="flex flex-col gap-1.5">
            <Label required for="country">{{ $t('Country') }}</Label>
            <Input type="text" id="country" v-model="form.personal_info_attributes.country" :placeholder="$t('Country')" />
            <p v-if="errors.country" class="text-destructive text-xs">
                {{ errors.country }}
            </p>
        </div>
    </fieldset>
</template>
